import { getCareSettingRoleKeys } from './careSettingProfiles.js';

const TRUST_ID = 'rsch_uhs';
const TRUST_NAME = 'University Hospitals Sussex NHS Foundation Trust';
const HOSPITAL_NAME = 'University Hospitals Sussex';

const COMPLETION = {
  compact: true,
  label: 'Journey summary',
  title: "You've reached the end of this guide",
  description: 'You do not need to open everything. Use the parts that are helpful to you and return whenever you need them.',
};

const PRIVATE_DEMO_DISCLAIMER =
  'This is a private draft demonstration and has not been approved by University Hospitals Sussex NHS Foundation Trust. It does not replace personalised advice. Always follow the instructions in your appointment or admission letter and the advice given by your healthcare team.';

const role = (id, title) => ({ id, title });

export const UH_SUSSEX_JOURNEY_DEMOS = Object.freeze({
  'uhs-ds-6H3K8T4M': {
    id: 'uhsussex-day-surgery',
    accessKey: 'uhs-ds-6H3K8T4M',
    status: 'draft',
    public: false,
    trustId: TRUST_ID,
    trustName: TRUST_NAME,
    hospitalName: HOSPITAL_NAME,
    title: 'Your Day Surgery Journey',
    eyebrow: 'Hospital Journey',
    introduction:
      'A private demonstration guide to help you prepare for day surgery at UHSussex, understand some of the people you may meet, and find approved Trust information about your operation, recovery and going home.',
    completion: COMPLETION,
    careSettingProfileKey: 'daySurgery',
    exploreMore: {
      locationFilter: 'theatres',
      title: "Can't find who you're looking for?",
      description: 'Day surgery involves a wider team depending on your procedure and individual needs.',
      linkLabel: 'See more people you might meet',
    },
    stages: [
      {
        id: 'before',
        title: 'Before you arrive',
        description: 'Use your admission letter and pre-operative advice to prepare for your procedure and journey to hospital.',
        phraseKeys: ['preAssessment', 'nilByMouth', 'consent'],
        linkIds: ['day-surgery', 'pre-op', 'hospitals'],
        questionIndexes: [0, 1, 2],
      },
      {
        id: 'procedure-recovery',
        title: 'Your procedure and recovery',
        description: 'Understand what may happen around your anaesthetic, operation and immediate recovery.',
        phraseKeys: ['cannula', 'theatre', 'recovery'],
        linkIds: ['anaesthetics'],
        questionIndexes: [3, 4],
      },
      {
        id: 'home',
        title: 'Going home',
        description: 'Prepare for transport home, support during the first 24 hours and safe recovery after day surgery.',
        phraseKeys: ['safetyNetting'],
        linkIds: ['day-surgery-discharge'],
        questionIndexes: [5, 6, 7],
      },
    ],
    roles: getCareSettingRoleKeys('daySurgery', { trustId: TRUST_ID }).map((id) => ({ id })),
    phraseKeys: ['preAssessment', 'nilByMouth', 'consent', 'cannula', 'theatre', 'recovery', 'safetyNetting'],
    usefulLinks: [
      {
        id: 'day-surgery',
        title: 'UHSussex day surgery',
        description: 'Official Trust information about day surgery services, locations and what to expect from a same-day admission.',
        url: 'https://www.uhsussex.nhs.uk/services/day-surgery/',
      },
      {
        id: 'pre-op',
        title: 'Your guide before your operation',
        description: 'UHSussex guidance about pre-operative assessment, fasting, medicines, preparing for surgery and prehabilitation.',
        url: 'https://www.uhsussex.nhs.uk/resources/your-guide-before-your-operation/',
      },
      {
        id: 'hospitals',
        title: 'Find your UHSussex hospital',
        description: 'Use the Trust hospital pages alongside your admission letter to plan where you need to go.',
        url: 'https://www.uhsussex.nhs.uk/hospitals/',
      },
      {
        id: 'anaesthetics',
        title: 'About anaesthetics',
        description: 'Official information about the UHSussex anaesthetic team and types of anaesthesia used for procedures and operations.',
        url: 'https://www.uhsussex.nhs.uk/services/anaesthetics/',
      },
      {
        id: 'day-surgery-discharge',
        title: 'Adult day surgery discharge information',
        description: 'General UHSussex advice about recovering safely at home after day surgery, including transport, pain, wounds and when to seek advice.',
        url: 'https://www.uhsussex.nhs.uk/resources/adult-day-surgery-discharge-information/',
      },
    ],
    questions: [
      'Where exactly do I need to go, and what time should I arrive?',
      'What should I eat, drink or do with my medicines before surgery?',
      'Who should I contact if I become unwell before my operation?',
      'Who will perform my procedure and who will look after my anaesthetic?',
      'What should I expect in recovery after the procedure?',
      'What needs to happen before I can go home?',
      'Do I need someone to take me home or stay with me afterwards?',
      'Who should I contact if I am worried after I get home?',
    ],
    disclaimer: PRIVATE_DEMO_DISCLAIMER,
  },

  'uhs-surg-9F2R6V5Q': {
    id: 'uhsussex-surgery-operation',
    accessKey: 'uhs-surg-9F2R6V5Q',
    status: 'draft',
    public: false,
    trustId: TRUST_ID,
    trustName: TRUST_NAME,
    hospitalName: HOSPITAL_NAME,
    title: 'Your Surgery Journey',
    eyebrow: 'Hospital Journey',
    introduction:
      'A private demonstration guide to help you prepare for an operation at UHSussex, understand the theatre and recovery team, and find approved information for before and after surgery.',
    completion: COMPLETION,
    exploreMore: {
      locationFilter: 'theatres',
      title: "Can't find who you're looking for?",
      description: 'The exact surgical team varies with your procedure, hospital and recovery needs.',
      linkLabel: 'See more people you might meet',
    },
    stages: [
      {
        id: 'preparing',
        title: 'Preparing for surgery',
        description: 'Prepare for pre-operative assessment, fasting, medicines, fitness for surgery and your admission day.',
        phraseKeys: ['preAssessment', 'nilByMouth', 'consent'],
        linkIds: ['pre-op', 'hospitals'],
        questionIndexes: [0, 1, 2],
      },
      {
        id: 'operation-day',
        title: 'Your operation day',
        description: 'Meet the team involved in your operation and understand common language used around theatre and anaesthesia.',
        phraseKeys: ['cannula', 'theatre'],
        linkIds: ['anaesthetics'],
        questionIndexes: [3, 4, 5],
      },
      {
        id: 'recovery',
        title: 'Recovery and going home',
        description: 'Understand immediate recovery, ward care and the planning that begins early for a safe discharge.',
        phraseKeys: ['recovery', 'safetyNetting'],
        linkIds: ['about-stay', 'discharge'],
        questionIndexes: [6, 7, 8],
      },
    ],
    roles: [
      role('surgeon', 'Surgeon'),
      role('anaesthetist', 'Anaesthetist'),
      role('operating_department_practitioner', 'Operating Department Practitioner'),
      role('theatre_nurse', 'Theatre Nurse'),
      role('theatre_recovery_nurse', 'Recovery Nurse'),
      role('staff_nurse', 'Staff Nurse'),
      role('healthcare_assistant', 'Healthcare Assistant'),
      role('physiotherapist', 'Physiotherapist'),
      role('pharmacist', 'Pharmacist'),
    ],
    phraseKeys: ['preAssessment', 'nilByMouth', 'consent', 'cannula', 'theatre', 'recovery', 'safetyNetting'],
    usefulLinks: [
      {
        id: 'pre-op',
        title: 'Your guide before your operation',
        description: 'UHSussex guidance about pre-operative assessment, fasting, medicines, prehabilitation and preparing for the day of surgery.',
        url: 'https://www.uhsussex.nhs.uk/resources/your-guide-before-your-operation/',
      },
      {
        id: 'hospitals',
        title: 'Find your UHSussex hospital',
        description: 'Hospital information to use with your admission letter when planning your journey and arrival.',
        url: 'https://www.uhsussex.nhs.uk/hospitals/',
      },
      {
        id: 'anaesthetics',
        title: 'About anaesthetics',
        description: 'Information about the anaesthetic team, types of anaesthesia and care before and after an operation.',
        url: 'https://www.uhsussex.nhs.uk/services/anaesthetics/',
      },
      {
        id: 'about-stay',
        title: 'About your hospital stay',
        description: 'Trust-wide information about preparing for an inpatient stay, special requirements and planning for discharge.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/about-your-stay/',
      },
      {
        id: 'discharge',
        title: 'Leaving hospital',
        description: 'UHSussex information about discharge planning, the discharge lounge and support for your next place of care.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/going-home/',
      },
    ],
    questions: [
      'What do I need to do to prepare for my operation?',
      'Should I change any of my medicines before surgery?',
      'What can I do before surgery to support my recovery?',
      'Who will be involved in my operation and anaesthetic?',
      'What type of anaesthetic is planned and what are my options?',
      'What should I expect immediately after the operation?',
      'How will pain, nausea and mobility be managed after surgery?',
      'What needs to happen before I can leave hospital?',
      'What follow-up or recovery support is likely to be arranged?',
    ],
    disclaimer: PRIVATE_DEMO_DISCLAIMER,
  },

  'uhs-adm-4J7N2C8P': {
    id: 'uhsussex-inpatient-admission',
    accessKey: 'uhs-adm-4J7N2C8P',
    status: 'draft',
    public: false,
    trustId: TRUST_ID,
    trustName: TRUST_NAME,
    hospitalName: HOSPITAL_NAME,
    title: 'Your Inpatient Admission Journey',
    eyebrow: 'Hospital Journey',
    introduction:
      'A private demonstration guide to help you prepare for a planned UHSussex admission, understand the ward team and know how discharge planning fits into your hospital stay.',
    completion: COMPLETION,
    stages: [
      {
        id: 'before-admission',
        title: 'Before admission',
        description: 'Use your admission letter to prepare for where to go, what to bring and which medicines to bring with you.',
        phraseKeys: ['admission'],
        linkIds: ['about-stay', 'what-to-bring', 'medicines', 'hospitals'],
        questionIndexes: [0, 1, 2],
      },
      {
        id: 'during-stay',
        title: 'During your stay',
        description: 'Understand day-to-day ward care, reviews and how different professionals contribute to your care plan.',
        phraseKeys: ['observations', 'wardRound', 'handover', 'review', 'carePlan'],
        linkIds: ['staying'],
        questionIndexes: [3, 4, 5],
      },
      {
        id: 'leaving-hospital',
        title: 'Planning to leave hospital',
        description: 'Discharge planning starts early so that medicines, transport, equipment and support can be arranged safely.',
        phraseKeys: ['medicallyFitForDischarge', 'dischargeSummary'],
        linkIds: ['leaving', 'discharge'],
        questionIndexes: [6, 7, 8],
      },
    ],
    roles: [
      role('consultant', 'Consultant'),
      role('resident_doctor_previously_referred_to_as_junior_doctor', 'Resident Doctor'),
      role('staff_nurse', 'Staff Nurse'),
      role('healthcare_assistant', 'Healthcare Assistant'),
      role('pharmacist', 'Pharmacist'),
      role('physiotherapist', 'Physiotherapist'),
      role('occupational_therapist', 'Occupational Therapist'),
      role('discharge_coordinator', 'Discharge Coordinator'),
    ],
    phraseKeys: ['admission', 'observations', 'wardRound', 'handover', 'review', 'carePlan', 'medicallyFitForDischarge', 'dischargeSummary'],
    usefulLinks: [
      {
        id: 'about-stay',
        title: 'About your stay',
        description: 'UHSussex information about preparing for an inpatient stay, reasonable adjustments and what to expect.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/about-your-stay/',
      },
      {
        id: 'what-to-bring',
        title: 'What to bring',
        description: 'A Trust guide to useful personal items, mobility or sensory aids, medicines and valuables during an inpatient stay.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/what-to-bring/',
      },
      {
        id: 'medicines',
        title: 'Medicines in hospital',
        description: 'Information about bringing regular medicines with you and how medicines are managed during admission and discharge.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/medicines/',
      },
      {
        id: 'hospitals',
        title: 'Find your UHSussex hospital',
        description: 'Hospital information to use alongside the location and arrival details in your admission letter.',
        url: 'https://www.uhsussex.nhs.uk/hospitals/',
      },
      {
        id: 'staying',
        title: 'Staying with us',
        description: 'Trust-wide information about inpatient care, staying active, food, medicines and preparing to go home.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/',
      },
      {
        id: 'leaving',
        title: 'Leaving hospital',
        description: 'Information about expected discharge dates, the discharge lounge and planning the next stage of your care.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/going-home/',
      },
      {
        id: 'discharge',
        title: 'Discharge and ongoing support',
        description: 'UHSussex information about leaving hospital safely, community referrals, support at home and discharge arrangements.',
        url: 'https://www.uhsussex.nhs.uk/services/discharge/',
      },
    ],
    questions: [
      'Where do I need to go and what should I bring with me?',
      'Should I bring all of my regular medicines?',
      'How can I tell the team about communication, mobility, sensory or other reasonable adjustment needs?',
      'Who is coordinating my care today?',
      'What is the plan for today and what are we waiting for next?',
      'Who should I ask if I do not understand a change in my care plan?',
      'What is my expected date of discharge?',
      'Will I need extra support, equipment or medicines when I leave?',
      'What do I need to know before I go home or to my next place of care?',
    ],
    disclaimer: PRIVATE_DEMO_DISCLAIMER,
  },

  'uhs-dis-5W8L3R7X': {
    id: 'uhsussex-discharge-recovery',
    accessKey: 'uhs-dis-5W8L3R7X',
    status: 'draft',
    public: false,
    trustId: TRUST_ID,
    trustName: TRUST_NAME,
    hospitalName: HOSPITAL_NAME,
    title: 'Your Discharge and Recovery Journey',
    eyebrow: 'Hospital Journey',
    introduction:
      'A private demonstration guide to help you understand UHSussex discharge planning, prepare for leaving hospital and keep the right questions and information together for recovery afterwards.',
    completion: COMPLETION,
    stages: [
      {
        id: 'getting-ready',
        title: 'Getting ready to leave',
        description: 'Work with your team on the expected date of discharge, medicines, transport and any support or equipment you may need.',
        phraseKeys: ['medicallyFitForDischarge', 'takeHomeMedicines', 'dischargeSummary'],
        linkIds: ['planning', 'discharge', 'medicines'],
        questionIndexes: [0, 1, 2],
      },
      {
        id: 'first-days-home',
        title: 'The first days after hospital',
        description: 'Make sure you understand your medicines, practical support and who to contact if something does not feel right.',
        phraseKeys: ['safetyNetting'],
        linkIds: ['leaving', 'transport'],
        questionIndexes: [3, 4, 5],
      },
      {
        id: 'follow-up',
        title: 'Recovery and follow-up',
        description: 'Keep track of follow-up plans, community support and questions that arise as you recover.',
        phraseKeys: ['followUpAppointment'],
        linkIds: ['discharge'],
        questionIndexes: [6, 7],
      },
    ],
    roles: [
      role('staff_nurse', 'Staff Nurse'),
      role('pharmacist', 'Pharmacist'),
      role('physiotherapist', 'Physiotherapist'),
      role('occupational_therapist', 'Occupational Therapist'),
      role('discharge_coordinator', 'Discharge Coordinator'),
      role('district_nurse', 'District Nurse'),
    ],
    phraseKeys: ['medicallyFitForDischarge', 'takeHomeMedicines', 'dischargeSummary', 'safetyNetting', 'followUpAppointment'],
    usefulLinks: [
      {
        id: 'planning',
        title: 'Planning together: leaving hospital when the time is right',
        description: 'UHSussex information about early discharge planning, expected discharge dates and involving the people important to you.',
        url: 'https://www.uhsussex.nhs.uk/resources/planning-together-leaving-hospital-when-the-time-is-right-2/',
      },
      {
        id: 'discharge',
        title: 'Discharge and ongoing support',
        description: 'Trust information about leaving hospital safely, support at home, onward care and discharge lounge arrangements.',
        url: 'https://www.uhsussex.nhs.uk/services/discharge/',
      },
      {
        id: 'medicines',
        title: 'Medicines when you go home',
        description: 'Information about discharge medicines, prescription changes and who can answer medicine questions.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/medicines/',
      },
      {
        id: 'leaving',
        title: 'Leaving hospital',
        description: 'UHSussex guidance about the day of discharge, the discharge lounge and getting to your next place of care.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/staying-hospital/going-home/',
      },
      {
        id: 'transport',
        title: 'Patient transport',
        description: 'Information about non-emergency patient transport for people whose medical condition or mobility makes other transport unsuitable.',
        url: 'https://www.uhsussex.nhs.uk/patients-and-visitors/patient-transport/',
      },
    ],
    questions: [
      'What needs to happen before I can leave hospital?',
      'What is my expected date of discharge and has anything changed?',
      'Have my medicines changed, and do I have everything I need?',
      'Will I need equipment, practical help or care at home?',
      'How am I getting home or to my next place of care?',
      'What symptoms or problems should make me seek advice after discharge?',
      'What follow-up or community support has been arranged?',
      'Who should I contact if I am worried or something in the discharge plan is unclear?',
    ],
    disclaimer: PRIVATE_DEMO_DISCLAIMER,
  },
});

